import { nothing, html, LitElement, TemplateResult, PropertyValues } from 'lit';
import { property, state } from 'lit/decorators.js';

import '@digital-realty/ix-icon/ix-icon.js';
import { findChildNodeById, updateNodeCheckedStatus } from './tree-utils.js';
import { IIxTreeNode } from './IIxTreeNode.js';
import { IxTreeStyles } from './ix-tree-styles.js';
import './ix-tree-node.js';
import { IxTreeTestIds as TestIds } from './constants/IxTreeTestIds.js';

export type Appearance = 'filled' | 'filled-tonal' | 'outlined' | 'default';

export class IxTree extends LitElement {
  static readonly styles = [IxTreeStyles];

  connectedCallback() {
    super.connectedCallback();
    this._activeItemId = this.selectedNodeId; // store in state
    this._selectedNodeId = this.selectedNodeId; // store in state
    this._rootNode = this.rootNode;
  }

  @property({ type: String }) ariaLabel: string = 'tree';

  @property({ type: Object }) rootNode!: IIxTreeNode;

  @property({ type: String }) selectedNodeId: string = '';

  @property({ type: Boolean }) checkboxesEnabled: boolean = false;

  @property({ type: String }) appearance: Appearance = 'default';

  @property({ type: Boolean }) hasIcons: boolean = false;

  @property({ type: Boolean }) removeChevron: boolean = false;

  @property({ type: Boolean }) allowMultiLine: boolean = false;

  @property({ type: Boolean }) rollup: boolean = true;

  @property({ type: Boolean }) selectNodeOnClick: boolean = false;

  @property({ type: Number }) refocus: number = 0;

  @property({ type: String }) lastFocusedNodeId: string = '';

  @property({ type: Boolean }) disableChildrenOnParentCheck: boolean = false;

  @property({ type: Boolean }) cascadeCheckToChildren: boolean = true;

  @property({ type: Boolean }) preventInnerTabbing: boolean = false;

  @state() private _activeItemId: string = '';

  @state() private _rootNode!: IIxTreeNode;

  @state() private _selectedNodeId: string = '';

  private onTreeNodeClick(e: CustomEvent): void {
    this.dispatchEvent(
      new CustomEvent('on-tree-node-selected', {
        detail: {
          message: e.detail.message,
        },
      })
    );

    this._selectedNodeId = e.detail.message.id;
    this._activeItemId = e.detail.message.id;
  }

  protected update(changedProperties: PropertyValues): void {
    super.update(changedProperties);
    // return focus to node after tree update (load more/tabbing from search input)
    if (changedProperties.has('refocus') && this.refocus > 0) {
      setTimeout(() => {
        this.focusTreeNode(this.lastFocusedNodeId);
      }, 50);
    }
  }

  private updateNodeExpandedState(
    nodeId: string,
    expanded: boolean
  ): IIxTreeNode | null {
    const nextRootNode = structuredClone(this._rootNode);
    const node = findChildNodeById(nextRootNode, nodeId);
    if (!node) {
      return null;
    }

    node.expanded = expanded;
    this._rootNode = nextRootNode;
    this.rootNode = nextRootNode;
    this._activeItemId = node.id;

    return node;
  }

  private onTreeNodeToggle(e: CustomEvent): void {
    const { id } = e.detail.message;
    const currentNode = findChildNodeById(this._rootNode, id);
    if (!currentNode) {
      return;
    }

    const updatedNode = this.updateNodeExpandedState(id, !currentNode.expanded);
    if (!updatedNode) {
      return;
    }

    this.dispatchEvent(
      new CustomEvent('on-tree-node-expand-toggle', {
        detail: {
          message: {
            node: updatedNode,
            id: updatedNode.id,
            label: updatedNode.label,
            parentId: updatedNode.parentId,
          },
        },
      })
    );
    this.requestUpdate();
  }

  private onTreeNodeCheck(e: CustomEvent): void {
    const { node } = e.detail.message;
    updateNodeCheckedStatus(
      node,
      this._rootNode,
      this.rollup,
      this.disableChildrenOnParentCheck,
      this.cascadeCheckToChildren
    );
    this._rootNode = structuredClone(this._rootNode);
    this.rootNode = this._rootNode;
    this.dispatchEvent(
      new CustomEvent('on-tree-node-checked', {
        detail: {
          message: e.detail.message,
        },
      })
    );

    this._activeItemId = e.detail.message.id;
  }

  private onTreeNodeFocus(e: CustomEvent): void {
    this._activeItemId = e.detail.message.id;
  }

  private getVisibleNodes(nodes: Array<IIxTreeNode> = []): Array<IIxTreeNode> {
    const visibleNodes: Array<IIxTreeNode> = [];

    for (const node of nodes) {
      visibleNodes.push(node);
      if (node.expanded && node.children?.length) {
        visibleNodes.push(...this.getVisibleNodes(node.children));
      }
    }

    return visibleNodes;
  }

  private getFocusableNodeId(visibleNodes: Array<IIxTreeNode>): string {
    const activeNode = visibleNodes.find(
      node => node.id === this._activeItemId && !node.disabled
    );
    if (activeNode) {
      return activeNode.id;
    }

    const selectedNode = visibleNodes.find(
      node => node.id === this._selectedNodeId && !node.disabled
    );
    if (selectedNode) {
      return selectedNode.id;
    }

    return visibleNodes.find(node => !node.disabled)?.id || '';
  }

  private static getNodeIdFromEvent(event: KeyboardEvent): string {
    const eventPath = event.composedPath();

    for (const pathItem of eventPath) {
      if (pathItem instanceof HTMLElement) {
        const nodeId = pathItem.dataset.id;
        if (nodeId) {
          return nodeId;
        }
      }
    }

    return '';
  }

  private focusTreeNode(nodeId: string): void {
    if (!nodeId) {
      return;
    }

    const renderedTreeNodes = Array.from(
      this.renderRoot.querySelectorAll('ix-tree-node')
    ) as Array<
      HTMLElement & { node?: IIxTreeNode; shadowRoot?: ShadowRoot | null }
    >;

    if (renderedTreeNodes.length === 0) {
      return;
    }

    let targetTreeNode = renderedTreeNodes.find(
      treeNode => treeNode.node?.id === nodeId
    );

    if (!targetTreeNode) {
      [targetTreeNode] = renderedTreeNodes;
    }

    const treeItem = targetTreeNode?.shadowRoot?.querySelector(
      '.tree-node-container[role="treeitem"]'
    ) as HTMLElement | null;

    treeItem?.focus();
  }

  private setNodeExpanded(nodeId: string, expanded: boolean): void {
    const node = this.updateNodeExpandedState(nodeId, expanded);
    if (!node) {
      return;
    }

    this.requestUpdate();
    this.dispatchEvent(
      new CustomEvent('on-tree-node-expand-toggle', {
        detail: {
          message: {
            node,
            id: node.id,
            label: node.label,
            parentId: node.parentId,
          },
        },
      })
    );
  }

  private handleTreeKeyDown(event: KeyboardEvent): void {
    if (event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }

    if (event.key === 'Tab') {
      // disable tab between tree nodes: tab to move focus out of tree to next focusable element
      // shift+tab to move focus to previous focusable element
      if (this.preventInnerTabbing) {
        event.preventDefault();
      }
      this.dispatchEvent(
        new CustomEvent('tab-navigation', {
          detail: {
            direction: event.shiftKey ? 'prev' : 'next',
          },
          bubbles: true,
          composed: true,
        })
      );
      return;
    }

    const visibleNodes = this.getVisibleNodes(this._rootNode.children);
    const enabledNodeIds = visibleNodes
      .filter(node => !node.disabled)
      .map(node => node.id);

    if (enabledNodeIds.length === 0) {
      return;
    }

    const focusedNodeId = IxTree.getNodeIdFromEvent(event);
    const fallbackNodeId = this.getFocusableNodeId(visibleNodes);
    const currentNodeId = focusedNodeId || fallbackNodeId;
    const currentNode = findChildNodeById(this._rootNode, currentNodeId);
    const currentIndex = Math.max(enabledNodeIds.indexOf(currentNodeId), 0);
    const [firstEnabledNodeId] = enabledNodeIds;

    let nextNodeId = '';

    if (
      event.key === 'ArrowLeft' &&
      currentNode &&
      currentNode.parentId === 'root' &&
      !currentNode.expanded
    ) {
      event.preventDefault();
      return;
    }

    if (event.key === 'ArrowRight' && currentNode?.children?.length) {
      event.preventDefault();
      if (!currentNode.expanded) {
        this.setNodeExpanded(currentNode.id, true);
      } else {
        const nextNode = findChildNodeById(
          this._rootNode,
          currentNode.children[0].id
        );
        if (nextNode && !nextNode.disabled) {
          this._activeItemId = nextNode.id;
          this.updateComplete.then(() => this.focusTreeNode(nextNode.id));
        }
      }
      return;
    }

    if (event.key === 'ArrowLeft') {
      if (currentNode?.expanded && currentNode.children?.length) {
        event.preventDefault();
        this.setNodeExpanded(currentNode.id, false);
        return;
      }

      if (currentNode?.parentId) {
        event.preventDefault();
        const parentNode = findChildNodeById(
          this._rootNode,
          currentNode.parentId
        );
        if (parentNode && !parentNode.disabled) {
          this._activeItemId = parentNode.id;
          this.updateComplete.then(() => this.focusTreeNode(parentNode.id));
        }
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      nextNodeId =
        enabledNodeIds[Math.min(currentIndex + 1, enabledNodeIds.length - 1)];
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      nextNodeId = enabledNodeIds[Math.max(currentIndex - 1, 0)];
    } else if (event.key === 'Home') {
      event.preventDefault();
      nextNodeId = firstEnabledNodeId;
    } else if (event.key === 'End') {
      event.preventDefault();
      nextNodeId = enabledNodeIds[enabledNodeIds.length - 1];
    }

    if (!nextNodeId || nextNodeId === currentNodeId) {
      return;
    }

    this._activeItemId = nextNodeId;
    this.updateComplete.then(() => this.focusTreeNode(nextNodeId));
  }

  private renderNodes(
    nodes: Array<IIxTreeNode> = [],
    level: number = 0,
    focusableNodeId: string = ''
  ): Array<TemplateResult<1>> {
    const renderedNodes: Array<TemplateResult<1>> = [];
    const setSize = nodes.length;

    for (const [index, node] of nodes.entries()) {
      const renderedNode = html`<ix-tree-node
        role="presentation"
        .level=${level}
        .node=${node}
        .posInSet=${index + 1}
        .setSize=${setSize}
        .appearance=${this.appearance}
        .selectNodeOnClick=${this.selectNodeOnClick}
        ?isActiveTreeNode=${this._activeItemId === node.id}
        ?isExpanded=${!!node.expanded}
        ?isSelected=${this._selectedNodeId === node.id}
        ?checkboxEnabled=${this.checkboxesEnabled}
        ?hasIcon=${this.hasIcons}
        ?removeChevron=${this.removeChevron}
        ?allowMultiLine=${this.allowMultiLine}
        ?disabled=${!!node.disabled}
        @on-tree-node-click=${this.onTreeNodeClick}
        @on-tree-node-toggle=${this.onTreeNodeToggle}
        @on-tree-node-check=${this.onTreeNodeCheck}
        @on-tree-node-focus=${this.onTreeNodeFocus}
        data-testid=${TestIds.NODE(node.id)}
      >
        ${node.expanded
          ? this.renderNodes(node.children, level + 1, focusableNodeId)
          : nothing}
      </ix-tree-node>`;
      renderedNodes.push(renderedNode);
    }

    return renderedNodes;
  }

  /**
   * Expand the active tree node and all of its parent nodes.
   */
  private expandActiveTreeNode(): void {
    const nodeToSelect =
      findChildNodeById(this._rootNode, this._activeItemId) || this._rootNode;

    let iteration = 0;
    const MAX_ITERATIONS = 1000;
    if (!nodeToSelect.parentId) {
      return;
    }
    let node: IIxTreeNode | null = findChildNodeById(
      this._rootNode,
      nodeToSelect.parentId
    );
    while (node && iteration < MAX_ITERATIONS) {
      node.expanded = true;
      if (!node.parentId) {
        break;
      }
      node = findChildNodeById(this._rootNode, node.parentId);
      iteration += 1;
    }
  }

  protected render(): TemplateResult<1> {
    this.expandActiveTreeNode();
    const visibleNodes = this.getVisibleNodes(this._rootNode.children);
    const focusableNodeId = this.getFocusableNodeId(visibleNodes);
    if (!this._activeItemId && focusableNodeId) {
      this._activeItemId = focusableNodeId;
    }

    return html`<div
      aria-label=${this.ariaLabel}
      class="ix-tree-container"
      role="tree"
      @keydown=${this.handleTreeKeyDown}
    >
      ${this.renderNodes(this._rootNode.children, 0, focusableNodeId)}
    </div>`;
  }
}
